// Persisted user preferences (localStorage): the small slice that should
// survive a reload — measurement display unit, dominant hand, and the
// grid/edges visibility toggles. Hotkey overrides persist separately
// (ui/hotkeys.ts); documents live in IndexedDB (model/idb-store.ts).

import type { Handedness } from "../interaction/tool";
import type { LengthUnit } from "../model/units";

const STORAGE_KEY = "tinydraft.settings.v1";

export interface PersistedSettings {
  displayUnit?: LengthUnit;
  dominantHand?: Handedness;
  gridLinesVisible?: boolean;
  edgesVisible?: boolean;
  smallScreenNoticeDismissed?: boolean;
  welcomeSeen?: boolean; // first-run welcome card dismissed (ui/welcome.ts)
  floorShowCollapsed?: boolean; // layout-mode "Show" section folded away
  // The bundled starter scene has been offered once (floor/starter-scene.ts).
  // Sticky by design: deleting the example must not bring it back next boot.
  starterSceneSeeded?: boolean;
}

// Parsed once, then kept in memory: some settings write from dial drags, and
// a getItem+parse round-trip per single-key write is pointless. localStorage
// is still the source of truth on load; writes go through the cache.
let _cache: PersistedSettings | null = null;

export function loadSettings(): PersistedSettings {
  if (_cache) return _cache;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    _cache = raw ? (JSON.parse(raw) as PersistedSettings) : {};
  } catch {
    _cache = {}; // unavailable/corrupt → defaults
  }
  return _cache;
}

export function saveSetting<K extends keyof PersistedSettings>(
  key: K,
  value: PersistedSettings[K],
): void {
  const cur = loadSettings();
  cur[key] = value;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cur));
  } catch {
    // localStorage blocked (private browsing) — settings just don't persist.
  }
}
